import { isAfter, isFuture } from 'date-fns'
import { Action, createAction, handleActions } from 'redux-actions'
import { all, call, cancel, put, select, takeLatest, takeLeading } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import { fetchSimpleAccountList } from '@/api/AccountApi'
import * as Bridge from '@/api/BridgeApi'
import { SCHEDULE_PAYMENT_API_URL } from '@/api/SchedulePaymentApi'
import {
  fetchSecureConfirmMessage,
  IGenerateSecureConfirmationMessageResponse,
} from '@/api/SecureConfirmApi'
import {
  fetchAccountOwnerBiFast,
  fetchTransferLimit,
  fetchTransferLimitByType,
} from '@/api/TransferApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import {
  EmptySimpleAccount,
  ISimpleAccountList,
  ISimpleAccountResponse,
} from '@/domains/AccountDomain'
import {
  ENTRY_ROUTE_QUERY,
  OKSign,
  SCHEDULE_PAYMENT_ROUTES,
  STATUS_CODE,
} from '@/domains/CommonDomain'
import {
  INFINITE_DATE,
  ISchedulePaymentFormState,
  ScheduleListRequestRowCount,
  SchedulePaymentDueType,
  SchedulePaymentHistoryParams,
  SchedulePaymentRepeatType,
  SchedulePaymentTransactionPurposeType,
} from '@/domains/SchedulePaymentDomain'
import { SecureTransactionType } from '@/domains/SecureConfirmDomain'
import {
  CreditAccountKindCode,
  EmptyFindTransferLimitResponse,
  IFindAccountOwnerBIFastDetail,
  IFindAccountOwnerBIFastRequest,
  IFindAccountOwnerBIFastResponse,
  IFindTransferLimitByTypeDetail,
  IFindTransferLimitByTypeResponse,
  IFindTransferLimitResponse,
  TransactionType,
} from '@/domains/TransferDomain'
import { IRootState } from '@/modules'
import { simpleAccountAsync, simpleAccountSelector } from '@/modules/account/SimpleAccountModule'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import { createFetchAction, createPostAction, createSaga } from '@/utils/actionUtils'
import { BRIDGE_ERROR_CODE } from '@/utils/bridgeUtils/constants'
import { IAccountInfoResult } from '@/utils/bridgeUtils/features/getMyAccount'
import { IWithdrawalAccountParams } from '@/utils/bridgeUtils/features/getWithdrawalAccount'
import {
  HostType,
  IRequestPinAuthResult,
  RequestPinAuthAction,
} from '@/utils/bridgeUtils/features/requestPinAuth'
import {
  getDayOfMonthNumber,
  getDayOfWeekNumber,
  getIsAfter,
  getIsBefore,
  getJakartaDate,
  getTomorrow,
} from '@/utils/dateUtils'
import { newError } from '@/utils/errorUtils'
import { getSchedulePaymentEndDt, getSchedulePaymentStartDt, INFINITE } from '@/utils/formatUtils'
import { checkEntry, getSchedulePaymentContext, redirect } from '@/utils/historyUtils'
import { $ } from '@/utils/i18nUtils'
import { toInteger } from '@/utils/numberUtils'
import { apiFailureReducer, apiSuccessReducer, reset } from '@/utils/reducerUtils'
import {
  isEmpty,
  isInvalidMonthInRange,
  isInvalidWeekdayInRange,
  isNull,
  isOrUnderLength,
  isSameBankTransaction,
  isUndefined,
  isValidRange,
  isValidSchedulePaymentDateFormat,
  isZero,
} from '@/utils/validationUtils'

import {
  schedulePaymentHistoryActions,
  schedulePaymentHistoryAsync,
} from './SchedulePaymentHistoryModule'
import { schedulePaymentActions, schedulePaymentAsync } from './SchedulePaymentModule'

type DateRangeState = Pick<ISchedulePaymentRegisterState, 'startDt' | 'endDt' | 'trsfDay'>

export interface ITrxLimitWarnInformation {
  limitAmount: number
  isOverMinTrxLimit: boolean
  isUnderMaxTrxLimit: boolean
}

export interface ISchedulePaymentFormValidationReport {
  isValidDueType: boolean
  isValidDueDay: boolean
  isValidTrxLimit: boolean
  isValidTrxAmt: boolean
  isValidAcctNo: boolean
  isValidDateRange: boolean
  isValidStartDt: boolean
  isValidStartDtFormat: boolean
  isValidEndDt: boolean
  isValidEndDtFormat: boolean
  isSenderAndReceiverAreSame: boolean
  isValidReceiverInformation: boolean
}

export interface ISchedulePaymentRegisterState extends ISchedulePaymentFormState {
  transferLimit: IFindTransferLimitResponse | IFindTransferLimitByTypeDetail
}

const initialState: ISchedulePaymentRegisterState = {
  transferLimit: EmptyFindTransferLimitResponse,
  myAcctYn: OKSign.NO,
  bnfcBankCd: '', // 수취은행코드
  bnfcBankNm: '', // 수취은행명
  bnfcAcctNo: '', // 수취계좌번호
  bnfcNm: '', // 수취인 이름 (crAcctNm1 + crAcctNm2)
  trxNote: '', // 거래노트
  dueType: undefined, // 기간유형 (D : 1회, W : 매주, M : 매월)
  trsfDay: undefined, // 지정일자 D (1회) : 0, W (매주) : 1~7, M (매월) : 1~31
  dueTimes: SchedulePaymentRepeatType.ONE_TIME, // 반복유형 (0:무한반복, 1:One Time)
  trxAmt: '0', // 거래금액
  acctNo: '', // 출금계좌 번호
  startDt: '', // 예약이체 시작일자
  endDt: '', // 예약이체 종료일자
  selectedAccount: null,
  isShowNumberPad: false,
  trscType: null,
  isDirty: false,
}

export const SCHEDULE_PAYMENT_REGISTER = 'SchedulePaymentRegister'

const GET_TRANSFER_LIMIT = `${SCHEDULE_PAYMENT_REGISTER}/transferLimit`
const INIT_ACCOUNT = `${SCHEDULE_PAYMENT_REGISTER}/setInitialAccount`
const SET_STATE = `${SCHEDULE_PAYMENT_REGISTER}/setState`
const TOGGLE_NUMBER_PAD = `${SCHEDULE_PAYMENT_REGISTER}/toggleNumberPad`
const SET_TRANSFER_LIMIT_OVER_SIGN = `${SCHEDULE_PAYMENT_REGISTER}/setTransferLimitOverSign`
const RESET = `${SCHEDULE_PAYMENT_REGISTER}/reset`
const SET_DUE_TYPE = `${SCHEDULE_PAYMENT_REGISTER}/setDueType`
const SET_NEVER_END = `${SCHEDULE_PAYMENT_REGISTER}/setNeverEnd`
const SET_WITHDRAWAL_ACCOUNT = `${SCHEDULE_PAYMENT_REGISTER}/setWithdrawalAccount`
const SET_SCHEDULE_PAYMENT_INFO_BY_QUERY = `${SCHEDULE_PAYMENT_REGISTER}/setSchedulePaymentInfoByQuery`
const SET_START_DATE = `${SCHEDULE_PAYMENT_REGISTER}/setStartDate`
const SET_END_DATE = `${SCHEDULE_PAYMENT_REGISTER}/setEndDate`
const SET_WEEK_DAY = `${SCHEDULE_PAYMENT_REGISTER}/setWeekDay`
const GET_TRANSFER_LIMIT_BY_TYPE = `${GET_TRANSFER_LIMIT}/by-type` // transfer limit for v2
const SCHEDULE_PAYMENT_REGISTER_BI_FAST = `${SCHEDULE_PAYMENT_REGISTER}/BI-Fast` // register logic for v2
export const SET_STATE_BY_ACCOUNT_OWNER_INFO = `${SCHEDULE_PAYMENT_REGISTER}/setStateByAccountOwnerInfo`

export const schedulePaymentRegisterActions = {
  setState: createAction<Partial<ISchedulePaymentRegisterState>>(SET_STATE),
  setInitialAccount: createAction(INIT_ACCOUNT),
  toggleNumberPad: createAction(TOGGLE_NUMBER_PAD),
  setTransferLimitOverSign: createAction(SET_TRANSFER_LIMIT_OVER_SIGN),
  reset: createAction(RESET),
  setDueType: createAction<Pick<ISchedulePaymentFormState, 'dueType'>>(SET_DUE_TYPE),
  setNeverEnd: createAction<{ isNeverEnd: boolean }>(SET_NEVER_END),
  setWithdrawalAccount: createAction(SET_WITHDRAWAL_ACCOUNT),
  setSchedulePaymentInfoByQuery: createAction(SET_SCHEDULE_PAYMENT_INFO_BY_QUERY),
  setStartDate: createAction(SET_START_DATE),
  setEndDate: createAction(SET_END_DATE),
  setWeekDay: createAction(SET_WEEK_DAY),
  setStateAccountOwnerInfoAsync: createAction<IFindAccountOwnerBIFastRequest>(
    SET_STATE_BY_ACCOUNT_OWNER_INFO,
  ),
}

export const schedulePaymentTransferLimitAsync = createFetchAction(GET_TRANSFER_LIMIT)
export const schedulePaymentRegisterAsync = createPostAction(SCHEDULE_PAYMENT_REGISTER)
export const schedulePaymentTransferLimitByTypeAsync = createFetchAction(GET_TRANSFER_LIMIT_BY_TYPE)
export const schedulePaymentRegisterBIFastAsync = createPostAction(
  SCHEDULE_PAYMENT_REGISTER_BI_FAST,
)

const reducer = {
  [schedulePaymentTransferLimitAsync.SUCCESS]: (
    state: ISchedulePaymentRegisterState,
    action: Action<any>,
  ): ISchedulePaymentRegisterState => {
    return {
      ...state,
      transferLimit: action.payload,
    }
  },
  [schedulePaymentTransferLimitAsync.FAILURE]: apiFailureReducer,
  [TOGGLE_NUMBER_PAD]: (state: ISchedulePaymentRegisterState): ISchedulePaymentRegisterState => {
    return {
      ...state,
      isShowNumberPad: !state.isShowNumberPad,
    }
  },
  [SET_STATE]: apiSuccessReducer,
  [RESET]: (state: ISchedulePaymentRegisterState) => reset(state, initialState),
  [schedulePaymentTransferLimitByTypeAsync.SUCCESS]: (
    state: ISchedulePaymentRegisterState,
    action: Action<IFindTransferLimitByTypeResponse>,
  ): ISchedulePaymentRegisterState => {
    const { onlineLimits, biFastLimits } = action.payload

    return {
      ...state,
      transferLimit: {
        ...state.transferLimit,
        ...(onlineLimits && onlineLimits),
        ...(biFastLimits && biFastLimits),
      },
    }
  },
  [schedulePaymentTransferLimitByTypeAsync.FAILURE]: apiFailureReducer,
}

export const schedulePaymentRegisterSelector = {
  allState: (state: IRootState): ISchedulePaymentRegisterState => state[SCHEDULE_PAYMENT_REGISTER],
  transferLimit: (state: IRootState) => {
    const { minTrxLimit, trxLimit, minTrxLimitOther, trxLimitOther } = state[
      SCHEDULE_PAYMENT_REGISTER
    ].transferLimit

    return {
      minTrxLimitValue: toInteger(minTrxLimit),
      maxTrxLimitValue: toInteger(trxLimit),
      minTrxLimitOtherValue: toInteger(minTrxLimitOther),
      maxTrxLimitOtherValue: toInteger(trxLimitOther),
    }
  },
  selectedAccount: (state: IRootState) => {
    const majorAccount = simpleAccountSelector.majorAccount(state)

    return oc(state[SCHEDULE_PAYMENT_REGISTER]).selectedAccount(majorAccount || EmptySimpleAccount)
  },
  isOverMinTrxLimit: (state: IRootState): boolean => {
    // 이체하고자 하는 금액이 최소 이체 금액 이상인가?
    const { trxAmt } = state[SCHEDULE_PAYMENT_REGISTER]
    const isSameBank = schedulePaymentRegisterSelector.isSameBank(state)
    const {
      minTrxLimitValue,
      minTrxLimitOtherValue,
    } = schedulePaymentRegisterSelector.transferLimit(state)

    if (isSameBank) {
      return toInteger(trxAmt) >= minTrxLimitValue
    }

    return toInteger(trxAmt) >= minTrxLimitOtherValue
  },
  isUnderMaxTrxLimit: (state: IRootState): boolean => {
    // 이체하고자 하는 금액이 최대 이체 금액 이하인가?
    const { trxAmt } = state[SCHEDULE_PAYMENT_REGISTER]
    const isSameBank = schedulePaymentRegisterSelector.isSameBank(state)
    const {
      maxTrxLimitValue,
      maxTrxLimitOtherValue,
    } = schedulePaymentRegisterSelector.transferLimit(state)

    if (isSameBank) {
      return toInteger(trxAmt) <= maxTrxLimitValue
    }

    return toInteger(trxAmt) <= maxTrxLimitOtherValue
  },
  isSameBank: (state: IRootState) => {
    // 당행이체인가?
    const { trscType } = schedulePaymentRegisterSelector.allState(state)

    return isSameBankTransaction(trscType as TransactionType)
  },
  isValidTrxLimit: (state: IRootState): boolean => {
    // 이체하고자 하는 금액이 이체 조건을 만족하는가?
    const isOverMinTrxLimit = schedulePaymentRegisterSelector.isOverMinTrxLimit(state)
    const isUnderMaxTrxLimit = schedulePaymentRegisterSelector.isUnderMaxTrxLimit(state)

    return isOverMinTrxLimit && isUnderMaxTrxLimit
  },
  isKeyPadCloseable: (state: IRootState): boolean => {
    const { trxAmt } = state[SCHEDULE_PAYMENT_REGISTER]
    const isValidTrxLimit: boolean = schedulePaymentRegisterSelector.isValidTrxLimit(state)

    return isValidTrxLimit && trxAmt !== '0'
  },
  isSenderAndReceiverAreSame: (state: IRootState): boolean => {
    const { acctNo, bnfcAcctNo } = schedulePaymentRegisterSelector.getRegisterFormState(state)

    return acctNo === bnfcAcctNo && !isEmpty(acctNo) && !isEmpty(bnfcAcctNo)
  },
  isValidReceiverInformation: (state: IRootState): boolean => {
    const {
      myAcctYn,
      bnfcBankCd,
      bnfcBankNm,
    } = schedulePaymentRegisterSelector.getRegisterFormState(state)
    if (myAcctYn === OKSign.YES) {
      return true
    }

    const isValidBnfcBankCd =
      !isUndefined(bnfcBankCd) && !isNull(bnfcBankCd) && !isEmpty(bnfcBankCd)
    const isValidBnfcBankNm =
      !isUndefined(bnfcBankNm) && !isNull(bnfcBankNm) && !isEmpty(bnfcBankNm)

    return isValidBnfcBankCd && isValidBnfcBankNm
  },
  isValid: (state: IRootState): boolean => {
    const {
      isValidDueType,
      isValidDueDay,
      isValidTrxLimit,
      isValidTrxAmt,
      isValidAcctNo,
      isValidDateRange,
      isValidStartDt,
      isValidStartDtFormat,
      isValidEndDt,
      isValidEndDtFormat,
      isSenderAndReceiverAreSame,
      isValidReceiverInformation,
    } = schedulePaymentRegisterSelector.formValidationErrorReport(state)

    const isValid =
      isValidDueType &&
      isValidDueDay &&
      isValidTrxLimit &&
      isValidTrxAmt &&
      isValidAcctNo &&
      isValidDateRange &&
      isValidStartDt &&
      isValidStartDtFormat &&
      isValidEndDt &&
      isValidEndDtFormat &&
      !isSenderAndReceiverAreSame &&
      isValidReceiverInformation

    return isValid
  },
  formValidationErrorReport: (state: IRootState): ISchedulePaymentFormValidationReport => {
    const {
      dueType,
      trxAmt,
      acctNo,
      startDt,
      endDt,
      dueTimes,
      trsfDay = 0,
    } = schedulePaymentRegisterSelector.getRegisterFormState(state)
    const isValidTrxLimit = schedulePaymentRegisterSelector.isValidTrxLimit(state)

    const isValidDueType = !isUndefined(dueType)
    // trsfDay: 지정일자 - D (1회) : 0, W (매주) : 1~7, M (매월) : 1~31 (29 ~ 31일은 1로 처리됨)
    const isValidDueDay = {
      D: isValidRange(0, 0)(trsfDay),
      W: isValidRange(1, 7)(trsfDay),
      M: isValidRange(1, 31)(trsfDay),
      undefined: false,
    }[dueType as SchedulePaymentDueType]
    const isValidAcctNo = !isZero(acctNo) && isOrUnderLength(`${acctNo}`, 19)
    const isValidTrxAmt = !isZero(trxAmt)
    const isValidDateRange = {
      D: startDt === endDt,
      W: isAfter(endDt, startDt),
      M: isAfter(endDt, startDt),
      undefined: false,
    }[dueType as SchedulePaymentDueType]
    const isValidStartDt = {
      D: isFuture(startDt),
      W: isFuture(startDt),
      M: isFuture(startDt),
      undefined: true,
    }[dueType as SchedulePaymentDueType]
    const isValidStartDtFormat = {
      D: isValidSchedulePaymentDateFormat(startDt),
      W: isValidSchedulePaymentDateFormat(startDt),
      M: isValidSchedulePaymentDateFormat(startDt),
      undefined: false,
    }[dueType as SchedulePaymentDueType]
    const isNeverEnd = dueTimes === SchedulePaymentRepeatType.INFINITE
    const isValidEndDt = {
      D: startDt === endDt,
      W: isNeverEnd ? endDt === INFINITE : isAfter(endDt, startDt),
      M: isNeverEnd ? endDt === INFINITE : isAfter(endDt, startDt),
      undefined: true,
    }[dueType as SchedulePaymentDueType]
    const isValidEndDtFormat = {
      D: isValidSchedulePaymentDateFormat(endDt),
      W: isValidSchedulePaymentDateFormat(endDt),
      M: isValidSchedulePaymentDateFormat(endDt),
      undefined: false,
    }[dueType as SchedulePaymentDueType]
    const isSenderAndReceiverAreSame = schedulePaymentRegisterSelector.isSenderAndReceiverAreSame(
      state,
    )
    const isValidReceiverInformation = schedulePaymentRegisterSelector.isValidReceiverInformation(
      state,
    )

    return {
      isValidDueType,
      isValidDueDay,
      isValidTrxLimit,
      isValidTrxAmt,
      isValidAcctNo,
      isValidDateRange,
      isValidStartDt,
      isValidStartDtFormat,
      isValidEndDt,
      isValidEndDtFormat,
      isSenderAndReceiverAreSame,
      isValidReceiverInformation,
    }
  },
  getRegisterFormState: (state: IRootState): ISchedulePaymentFormState => {
    const {
      startDt,
      endDt,
      dueType,
      transferLimit,
      trxAmt,
      ...rest
    } = schedulePaymentRegisterSelector.allState(state)

    return {
      startDt: getSchedulePaymentStartDt(startDt),
      endDt: getSchedulePaymentEndDt(startDt, endDt, dueType),
      dueType,
      trxAmt: trxAmt.toString(),
      ...rest,
    }
  },
  isNeverEnd: (state: IRootState): boolean => {
    const { dueTimes } = schedulePaymentRegisterSelector.allState(state)

    return dueTimes === SchedulePaymentRepeatType.INFINITE
  },
  trxLimitWarnInformation: (state: IRootState): ITrxLimitWarnInformation => {
    const isOverMinTrxLimit = schedulePaymentRegisterSelector.isOverMinTrxLimit(state)
    const isUnderMaxTrxLimit = schedulePaymentRegisterSelector.isUnderMaxTrxLimit(state)
    const isSameBank = schedulePaymentRegisterSelector.isSameBank(state)
    const {
      minTrxLimitValue,
      maxTrxLimitValue,
      minTrxLimitOtherValue,
      maxTrxLimitOtherValue,
    } = schedulePaymentRegisterSelector.transferLimit(state)
    let limitAmount

    if (!isOverMinTrxLimit) {
      limitAmount = isSameBank ? minTrxLimitValue : minTrxLimitOtherValue
    } else if (!isUnderMaxTrxLimit) {
      limitAmount = isSameBank ? maxTrxLimitValue : maxTrxLimitOtherValue
    } else {
      limitAmount = 0
    }

    return {
      limitAmount,
      isOverMinTrxLimit,
      isUnderMaxTrxLimit,
    }
  },
  isTimingToDisplayAmountError: (state: IRootState): boolean => {
    // 입력 금액의 에러를 사용자에게 표시할 타이밍인가?
    const { isDirty } = schedulePaymentRegisterSelector.allState(state)
    const isValidTrxLimit = schedulePaymentRegisterSelector.isValidTrxLimit(state)

    return !isValidTrxLimit && isDirty
  },
  defaultDateRange: (state: IRootState): DateRangeState => {
    const { dueType } = schedulePaymentRegisterSelector.allState(state)

    switch (dueType) {
      case SchedulePaymentDueType.DAY:
      default:
        return {
          startDt: '',
          endDt: '',
          trsfDay: 0,
        }
      case SchedulePaymentDueType.WEEK:
        return {
          startDt: '',
          endDt: '',
          trsfDay: getDayOfWeekNumber(new Date()),
        }
      case SchedulePaymentDueType.MONTH:
        return {
          startDt: '',
          endDt: '',
          trsfDay: getDayOfMonthNumber(getTomorrow()),
        }
    }
  },
  isBIFast: (state: IRootState): boolean => {
    const { bnfcAcctKindCd } = schedulePaymentRegisterSelector.allState(state)

    return bnfcAcctKindCd === CreditAccountKindCode.BI_FAST_ACCOUNT_NO
  },
}

export function* register() {
  try {
    const registerFormState: ISchedulePaymentFormState = yield select(
      schedulePaymentRegisterSelector.getRegisterFormState,
    )
    const {
      dueTimes,
      isDirty,
      isShowNumberPad,
      selectedAccount,
      trscType,
      bnfcBankCd,
      bnfcBankNm,
      /* v1이더라도, recently-favorite list 를 통한 유입이면 BI-Fast info 등록 가능 */
      biPurpCd, // only v2
      bnfcAcctItmCd, // only v2
      bnfcAcctKindCd, // only v2
      ...params
    } = registerFormState
    const requestPayload =
      params.myAcctYn === OKSign.YES ? params : { bnfcBankCd, bnfcBankNm, ...params }
    const secureTransactionType = SecureTransactionType.SCHEDULED_TRANSFER
    const secureConfirmationMessage: IGenerateSecureConfirmationMessageResponse = yield call(
      fetchSecureConfirmMessage,
      secureTransactionType,
      requestPayload,
    )
    const { result }: IRequestPinAuthResult = yield call(Bridge.enterTransactionPinForTransfer, {
      hostType: HostType.BANK_API,
      apiUrl: SCHEDULE_PAYMENT_API_URL.REGISTER,
      request: {
        secureConfirmation: {
          ...secureConfirmationMessage,
          secureTransaction: secureTransactionType,
        },
        ...requestPayload,
      },
    })

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR
    ) {
      // const { code } = JSON.parse(result.response)
      // throw newError(code)
      throw JSON.parse(result.response)
    }

    if (result.action === RequestPinAuthAction.AUTH_SUCCESS) {
      yield all([
        call(Bridge.showNotification, { message: $('schedulepayment_addsuccess_toast') }),
        call(redirect, `${SCHEDULE_PAYMENT_ROUTES.ROOT}?${ENTRY_ROUTE_QUERY}`),
      ])
      yield all([
        put(schedulePaymentHistoryActions.resetState()),
        put(schedulePaymentActions.resetState()),
        put(schedulePaymentRegisterActions.reset()),
      ])

      yield all([
        put(
          schedulePaymentAsync.fetch({
            page: 1,
            rowCnt: ScheduleListRequestRowCount,
          }),
        ),
        put(
          schedulePaymentHistoryAsync.fetch({
            page: 1,
            rowCnt: ScheduleListRequestRowCount,
          }),
        ),
      ])
    }
  } catch (e) {
    if (e.code === BRIDGE_ERROR_CODE.SECURE_CONFIRMATION_CANCELED) {
      return
    }

    yield put(errorActions.set(e))
  }
}

export function* registerBIFast() {
  try {
    const registerFormState: ISchedulePaymentFormState = yield select(
      schedulePaymentRegisterSelector.getRegisterFormState,
    )
    const isLineBank: boolean = yield select(schedulePaymentRegisterSelector.isSameBank)
    const {
      dueTimes,
      isDirty,
      isShowNumberPad,
      selectedAccount,
      trscType,
      bnfcBankCd,
      bnfcBankNm,
      ...params
    } = registerFormState
    const requestPayload =
      params.myAcctYn === OKSign.YES
        ? { isLineBank, ...params }
        : { isLineBank, bnfcBankCd, bnfcBankNm, ...params }
    const secureTransactionType = SecureTransactionType.SCHEDULED_TRANSFER_VER2
    const secureConfirmationMessage: IGenerateSecureConfirmationMessageResponse = yield call(
      fetchSecureConfirmMessage,
      secureTransactionType,
      requestPayload,
    )
    const { result }: IRequestPinAuthResult = yield call(Bridge.enterTransactionPinForTransfer, {
      hostType: HostType.BANK_API,
      apiUrl: SCHEDULE_PAYMENT_API_URL.REGISTER,
      request: {
        secureConfirmation: {
          ...secureConfirmationMessage,
          secureTransaction: secureTransactionType,
        },
        ...requestPayload,
      },
    })

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR
    ) {
      throw JSON.parse(result.response)
    }

    if (result.action === RequestPinAuthAction.AUTH_SUCCESS) {
      yield all([
        call(Bridge.showNotification, { message: $('schedulepayment_addsuccess_toast') }),
        call(redirect, `${SCHEDULE_PAYMENT_ROUTES.ROOT}?${ENTRY_ROUTE_QUERY}`),
      ])
      yield all([
        put(schedulePaymentHistoryActions.resetState()),
        put(schedulePaymentActions.resetState()),
        put(schedulePaymentRegisterActions.reset()),
      ])

      yield all([
        put(
          schedulePaymentAsync.fetch({
            page: 1,
            rowCnt: ScheduleListRequestRowCount,
          }),
        ),
        put(
          schedulePaymentHistoryAsync.fetch({
            page: 1,
            rowCnt: ScheduleListRequestRowCount,
          }),
        ),
      ])
    }
  } catch (e) {
    if (e.code === BRIDGE_ERROR_CODE.SECURE_CONFIRMATION_CANCELED) {
      return
    }

    yield put(errorActions.set(e))
  }
}

export function* setInitialAccount() {
  const majorAccount = yield select(simpleAccountSelector.majorAccount)
  const { acctNo } = yield select(schedulePaymentRegisterSelector.allState)

  if (isEmpty(acctNo)) {
    yield put(
      schedulePaymentRegisterActions.setState({
        acctNo: majorAccount.acctNo,
        selectedAccount: majorAccount,
      }),
    )
  }
}

export function* setDueType(action: Action<Pick<ISchedulePaymentFormState, 'dueType'>>) {
  yield put(schedulePaymentRegisterActions.setState(action.payload))
  const defaultDateRange = yield select(schedulePaymentRegisterSelector.defaultDateRange)
  yield put(schedulePaymentRegisterActions.setState(defaultDateRange))
}

export function* setNeverEnd(action: Action<{ isNeverEnd: boolean }>) {
  const { isNeverEnd } = action.payload
  const newState = {
    endDt: isNeverEnd ? INFINITE_DATE : '',
    dueTimes: isNeverEnd ? SchedulePaymentRepeatType.INFINITE : SchedulePaymentRepeatType.ONE_TIME,
  }

  yield put(schedulePaymentRegisterActions.setState(newState))
}

export function* setWithdrawalAccount() {
  try {
    const { acctNo: selectedAccountNo }: ISimpleAccountResponse = yield select(
      schedulePaymentRegisterSelector.selectedAccount,
    )
    const param: IWithdrawalAccountParams = {
      title: $('selectaccount_withdrawal_layermenu_title'),
      selectedAccountNo,
      amount: '0',
    }
    const { result: selectedAccount }: IAccountInfoResult = yield call(
      Bridge.getWithdrawalAccount,
      param,
    )

    if (selectedAccount) {
      yield put(
        schedulePaymentRegisterActions.setState({
          acctNo: selectedAccount.acctNo,
          selectedAccount,
        }),
      )
    }
  } catch (e) {
    if (e.code === BRIDGE_ERROR_CODE.CLOSE) {
      yield cancel()
    }

    yield put(errorActions.set(e))
  }
}

export function* setSchedulePaymentInfoByQuery() {
  try {
    yield put(loadingActions.start(SCHEDULE_PAYMENT_REGISTER))
    const isEntry = yield call(checkEntry)

    if (!isEntry) {
      return
    }

    const params: SchedulePaymentHistoryParams = yield call(getSchedulePaymentContext)
    const { trscType } = params

    // app 에서 전달받은 값
    const params2: ISchedulePaymentRegisterState = yield select(
      schedulePaymentRegisterSelector.allState,
    )

    if (!trscType) {
      throw newError(STATUS_CODE.TRANSFER_CLIENT_ERROR)
    }

    const accountList: ISimpleAccountList = yield call(fetchSimpleAccountList)

    if (params2.bnfcAcctNo !== '' && params.bnfcAcctNo !== params2.bnfcAcctNo) {
      yield put(schedulePaymentRegisterActions.setState(params2))
    } else {
      yield put(schedulePaymentRegisterActions.setState(params))
    }

    yield all([
      put(simpleAccountAsync.success(accountList)),
      //put(schedulePaymentRegisterActions.setState(params)),
      put(schedulePaymentRegisterActions.setInitialAccount()),
    ])

    yield put(
      schedulePaymentRegisterActions.setStateAccountOwnerInfoAsync({
        crBnkCd: params.bnfcBankCd,
        crAcctNo: params.bnfcAcctNo,
        crAcctItmCd: params.bnfcAcctItmCd,
        crAcctKindDvCd: params.bnfcAcctKindCd,
      }),
    )
  } catch (e) {
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(SCHEDULE_PAYMENT_REGISTER))
  }
}

function buildAccountOwnerInfo(
  data: IFindAccountOwnerBIFastDetail,
): Partial<ISchedulePaymentRegisterState> {
  return {
    bnfcBankCd: data.crBnkCd,
    ...(data.crBnkNm && { bnfcBankNm: data.crBnkNm }), // bi인 경우에만 crBnkNm 값 들어옴, online은 기존 값 사용
    bnfcAcctNo: data.crAcctNo,
    bnfcAcctKindCd: data.crAcctKindDvCd,
    bnfcAcctItmCd: data.crAcctItmCd,
  }
}

export function* setStateByAccountOwnerInfo(action: Action<IFindAccountOwnerBIFastRequest>) {
  // 계좌 소유주 정보 취득 (Prima | Bi-Fast) -> BI 우선 정보 지정
  try {
    yield put(loadingActions.start(SET_STATE_BY_ACCOUNT_OWNER_INFO))

    const { biAccount, onlineAccount }: IFindAccountOwnerBIFastResponse = yield call(
      fetchAccountOwnerBiFast,
      action.payload,
    )
    let accountOwnerInfo: Partial<ISchedulePaymentRegisterState> = {}
    if (!biAccount) {
      if (!onlineAccount) return
      accountOwnerInfo = buildAccountOwnerInfo(onlineAccount)
    } else {
      accountOwnerInfo = {
        ...buildAccountOwnerInfo(biAccount),
        biPurpCd: SchedulePaymentTransactionPurposeType.FUND_TRANSFER,
      }
    }
    yield put(schedulePaymentRegisterActions.setState(accountOwnerInfo))
  } catch (e) {
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(SET_STATE_BY_ACCOUNT_OWNER_INFO))
  }
}

export function* setWeekDay(action: Action<number>) {
  const trsfDay = action.payload
  const { startDt, endDt }: ISchedulePaymentRegisterState = yield select(
    schedulePaymentRegisterSelector.allState,
  )
  if (isInvalidWeekdayInRange(startDt, endDt)) {
    yield put(layerActions.open(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR))

    return
  }

  yield put(schedulePaymentRegisterActions.setState({ trsfDay }))
}

export function* setStartDate(action: Action<{ date: string }>) {
  const { date } = action.payload
  const { endDt, dueType }: ISchedulePaymentRegisterState = yield select(
    schedulePaymentRegisterSelector.allState,
  )
  const today = getJakartaDate()
  const isWeekDueType = dueType === SchedulePaymentDueType.WEEK
  const isMonthDueType = dueType === SchedulePaymentDueType.MONTH
  let transferDay = 0
  if (dueType === SchedulePaymentDueType.WEEK) {
    transferDay = new Date(date).getDay()
  } else {
    transferDay = toInteger(date.slice(-2))
  }

  if (getIsBefore(date, today)) {
    const leaveAlertType = isWeekDueType
      ? LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_ERROR
      : LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_ERROR

    yield put(layerActions.open(leaveAlertType))

    return
  }

  if (endDt && !getIsAfter(endDt, date)) {
    const leaveAlertType = isWeekDueType
      ? LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_DATE_AND_END_DATE_ERROR
      : LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_START_MONTH_AND_END_MONTH_ERROR

    yield put(layerActions.open(leaveAlertType))

    return
  }

  if (isWeekDueType && isInvalidWeekdayInRange(date, endDt)) {
    yield put(layerActions.open(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR))

    return
  }

  if (isMonthDueType && isInvalidMonthInRange(endDt, date)) {
    yield put(layerActions.open(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_MONTH_DATE_ERROR))

    return
  }

  yield put(schedulePaymentRegisterActions.setState({ trsfDay: transferDay }))
  yield put(schedulePaymentRegisterActions.setState({ startDt: date }))
}

export function* setEndDate(action: Action<{ date: string }>) {
  const { date } = action.payload
  const { startDt, dueType }: ISchedulePaymentRegisterState = yield select(
    schedulePaymentRegisterSelector.allState,
  )
  const isWeekDueType = dueType === SchedulePaymentDueType.WEEK
  const isMonthDueType = dueType === SchedulePaymentDueType.MONTH

  if (getIsBefore(date, startDt)) {
    const leaveAlertType = isWeekDueType
      ? LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_DATE_ERROR
      : LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_END_MONTH_ERROR

    yield put(layerActions.open(leaveAlertType))

    return
  }

  if (isWeekDueType && isInvalidWeekdayInRange(startDt, date)) {
    yield put(layerActions.open(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_WEEKEND_DATE_ERROR))

    return
  }

  if (isMonthDueType && isInvalidMonthInRange(startDt, date)) {
    yield put(layerActions.open(LEAVE_ALERT_TYPE.SCHEDULE_PAYMENT_MONTH_DATE_ERROR))

    return
  }

  yield put(schedulePaymentRegisterActions.setState({ endDt: date }))
}

export const schedulePaymentRegisterReducer = handleActions(reducer, initialState)
export const schedulePaymentRegisterSaga = [
  takeLatest(
    schedulePaymentTransferLimitAsync.FETCH,
    createSaga(schedulePaymentTransferLimitAsync, fetchTransferLimit),
  ),
  takeLatest(
    schedulePaymentTransferLimitByTypeAsync.FETCH,
    createSaga(schedulePaymentTransferLimitByTypeAsync, fetchTransferLimitByType),
  ),
  takeLeading(
    schedulePaymentRegisterAsync.POST,
    createSaga(schedulePaymentRegisterAsync, register),
  ),
  takeLeading(
    schedulePaymentRegisterBIFastAsync.POST,
    createSaga(schedulePaymentRegisterBIFastAsync, registerBIFast),
  ),
  takeLatest(INIT_ACCOUNT, setInitialAccount),
  takeLatest(SET_DUE_TYPE, setDueType),
  takeLatest(SET_NEVER_END, setNeverEnd),
  takeLatest(SET_WITHDRAWAL_ACCOUNT, setWithdrawalAccount),
  takeLatest(SET_SCHEDULE_PAYMENT_INFO_BY_QUERY, setSchedulePaymentInfoByQuery),
  takeLatest(SET_START_DATE, setStartDate),
  takeLatest(SET_END_DATE, setEndDate),
  takeLatest(SET_WEEK_DAY, setWeekDay),
  takeLatest(SET_STATE_BY_ACCOUNT_OWNER_INFO, setStateByAccountOwnerInfo),
]
